import { z } from 'zod';

import { getJson } from '@/lib/api/client';
import { apiRevalidateSeconds } from '@/lib/env';
import { logError } from '@/lib/logger';

export type PupNameItem = {
  id: number;
  name: string;
  sex: string | null;
  size: string | null;
  color: string | null;
  personality: string | null;
  ambition: string | null;
  belief: string | null;
  season: string | null;
  historicalFigure: string | null;
  favouriteColor: string | null;
  animal: string | null;
  kind: string | null;
  culture: string | null;
};

const pupNameItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  sex: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  personality: z.string().nullable().optional(),
  ambition: z.string().nullable().optional(),
  belief: z.string().nullable().optional(),
  season: z.string().nullable().optional(),
  historicalFigure: z.string().nullable().optional(),
  favouriteColor: z.string().nullable().optional(),
  animal: z.string().nullable().optional(),
  kind: z.string().nullable().optional(),
  culture: z.string().nullable().optional()
});

const pupNamesResponseSchema = z.object({
  items: z.array(pupNameItemSchema)
});

export const fetchPupNamesForGenerator = async (options?: {
  cache?: boolean;
}): Promise<PupNameItem[]> => {
  try {
    const cacheable = options?.cache !== false;
    const result = await getJson<
      typeof pupNamesResponseSchema,
      z.infer<typeof pupNamesResponseSchema>
    >('/catalog/pup-names/generator', {
      schema: pupNamesResponseSchema,
      next: cacheable
        ? { revalidate: apiRevalidateSeconds, tags: ['catalog:pup-names'] }
        : undefined,
      cache: cacheable ? undefined : 'no-store'
    });

    return result.items.map((item) => ({
      id: item.id,
      name: item.name,
      sex: item.sex ?? null,
      size: item.size ?? null,
      color: item.color ?? null,
      personality: item.personality ?? null,
      ambition: item.ambition ?? null,
      belief: item.belief ?? null,
      season: item.season ?? null,
      historicalFigure: item.historicalFigure ?? null,
      favouriteColor: item.favouriteColor ?? null,
      animal: item.animal ?? null,
      kind: item.kind ?? null,
      culture: item.culture ?? null
    }));
  } catch (error) {
    logError('[fetchPupNamesForGenerator] Failed to load pup names', error);
    return [];
  }
};
